import { getCookieValue } from '@/js/helpers/cookieHelpers';
import { getProjectId } from '@/js/pages/helpers/tracking-script/projectId';
import { ImpressionBatchPayload, ImpressionEventPayload } from '@/js/pages/helpers/tracking-script/types';
import { impression as sendImpressionBatch } from './api';

const IMPRESSION_ATTRIBUTE = 'data-impression';
const IMPRESSION_SELECTOR = `[${IMPRESSION_ATTRIBUTE}]`;

// An ad counts as seen when >= 50% of it is visible for >= 1 second.
const VISIBILITY_RATIO = 0.5;
const VISIBLE_DURATION_MS = 1000;

// Batch rules: flush on a short interval, or immediately once enough events queue up.
const FLUSH_INTERVAL_MS = 2000;
const MAX_BATCH_SIZE = 10;

interface ElementState {
    intersecting: boolean;
    timer: ReturnType<typeof setTimeout> | null;
}

const queue: ImpressionEventPayload[] = [];
const elementState = new Map<Element, ElementState>();
const countedElements = new WeakSet<Element>();
const countedManualTags = new Set<string>();

let intersectionObserver: IntersectionObserver | null = null;
let mutationObserver: MutationObserver | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;

const getTrackingCookieId = (): string | null => {
    const cookieValue = getCookieValue('vulpr');
    if (!cookieValue) {
        return null;
    }

    try {
        return JSON.parse(cookieValue).client_id ?? null;
    } catch (e) {
        return null;
    }
};

const getGaCid = (): string | null => {
    const gaCookie = getCookieValue('_ga');
    if (!gaCookie) {
        return null;
    }

    const parts = gaCookie.split('.');

    return parts.length > 2 ? `${parts[parts.length - 2]}.${parts[parts.length - 1]}` : null;
};

// Normalized path, keeping locale prefix and query string (origin/host stripped).
const getPageUrl = (): string => window.location.pathname + window.location.search;

// Only placements listed in `config/impression-tracking.php` (surfaced as a global)
// may record impressions. Matching is prefix-based so entries like `popup-` cover
// every dynamic campaign suffix. An empty/missing allowlist tracks nothing.
const isPlacementAllowed = (tag: string): boolean => {
    const allowlist = window.vulprImpressionAllowlist;

    if (!Array.isArray(allowlist) || allowlist.length === 0) {
        return false;
    }

    return allowlist.some((prefix) => tag === prefix || tag.startsWith(prefix));
};

// Human-readable placement name resolved once, at the single enqueue choke point, from the
// `config/impression-tracking.php` labels surfaced as a global. Keyed by the tag's leading
// family (segment before the first `-`). Sent with the event so Vulpr stores/displays it
// instead of redefining the taxonomy on its side.
const resolveLabel = (tag: string): string | null => {
    const labels = window.vulprImpressionLabels;
    if (labels == null) {
        return null;
    }

    const family = tag.split('-', 1)[0];

    return labels[family] ?? null;
};

// The goon slug behind the placement (everything after the leading family). Sent so Vulpr can
// bind the impression to a real link at ingest and attribute it to a partner, instead of
// re-deriving the link from the tag at report time.
const resolveGoonSlug = (tag: string): string | null => {
    const separatorIndex = tag.indexOf('-');

    return separatorIndex === -1 ? null : tag.slice(separatorIndex + 1);
};

const flush = (useBeacon = false): void => {
    if (flushTimer !== null) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }

    if (queue.length === 0) {
        return;
    }

    const events = queue.splice(0, queue.length);

    const payload: ImpressionBatchPayload = {
        project_id: getProjectId(),
        tracking_cookie_id: getTrackingCookieId(),
        ga_cid: getGaCid(),
        session_id: window.vulprSid ?? null,
        events,
    };

    const dispatched = sendImpressionBatch(payload, useBeacon);

    // If a beacon send failed (e.g. payload too large), re-queue so a later flush retries.
    if (!dispatched && useBeacon) {
        queue.unshift(...events);
    }
};

const scheduleFlush = (): void => {
    if (flushTimer === null) {
        flushTimer = setTimeout(() => flush(false), FLUSH_INTERVAL_MS);
    }
};

const enqueue = (tag: string): void => {
    if (!isPlacementAllowed(tag)) {
        return;
    }

    queue.push({
        event_id: crypto.randomUUID(),
        tag,
        label: resolveLabel(tag),
        goon_slug: resolveGoonSlug(tag),
        page_url: getPageUrl(),
        occurred_at: new Date().toISOString(),
    });

    if (queue.length >= MAX_BATCH_SIZE) {
        flush(false);

        return;
    }

    scheduleFlush();
};

const commitElement = (element: Element): void => {
    const tag = element.getAttribute(IMPRESSION_ATTRIBUTE);
    const state = elementState.get(element);

    if (state?.timer) {
        clearTimeout(state.timer);
    }

    elementState.delete(element);
    countedElements.add(element);
    intersectionObserver?.unobserve(element);

    if (tag) {
        enqueue(tag);
    }
};

// Start/stop the 1s dwell timer based on whether the element is currently eligible
// (>= 50% visible AND the tab is in the foreground).
const evaluateElement = (element: Element): void => {
    if (countedElements.has(element)) {
        return;
    }

    const state = elementState.get(element);
    if (!state) {
        return;
    }

    const eligible = state.intersecting && document.visibilityState === 'visible';

    if (eligible && state.timer === null) {
        state.timer = setTimeout(() => commitElement(element), VISIBLE_DURATION_MS);
    } else if (!eligible && state.timer !== null) {
        clearTimeout(state.timer);
        state.timer = null;
    }
};

const handleIntersections: IntersectionObserverCallback = (entries) => {
    entries.forEach((entry) => {
        const state = elementState.get(entry.target);
        if (!state) {
            return;
        }

        state.intersecting = entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_RATIO;
        evaluateElement(entry.target);
    });
};

const observeElement = (element: Element): void => {
    if (countedElements.has(element) || elementState.has(element)) {
        return;
    }

    elementState.set(element, { intersecting: false, timer: null });
    intersectionObserver?.observe(element);
};

const scanForElements = (root: ParentNode = document): void => {
    root.querySelectorAll(IMPRESSION_SELECTOR).forEach(observeElement);
};

const handleMutations: MutationCallback = (mutations) => {
    mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) {
                return;
            }

            if (node.matches(IMPRESSION_SELECTOR)) {
                observeElement(node);
            }

            scanForElements(node);
        });
    });
};

const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
        flush(true);
    }

    elementState.forEach((_state, element) => evaluateElement(element));
};

/**
 * Manually record an impression for a placement that is not naturally visible on
 * page load (popup, modal, countdown, bell notification). Counted once per page view.
 */
export const trackImpression = (tag: string): void => {
    if (!tag || countedManualTags.has(tag)) {
        return;
    }

    countedManualTags.add(tag);
    enqueue(tag);
};

export const initImpressionTracker = (): void => {
    if (initialized || typeof window === 'undefined') {
        return;
    }

    if (!import.meta.env.VITE_VULPR_BASE_URL || !('IntersectionObserver' in window)) {
        return;
    }

    initialized = true;

    intersectionObserver = new IntersectionObserver(handleIntersections, {
        threshold: [VISIBILITY_RATIO],
    });

    mutationObserver = new MutationObserver(handleMutations);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    scanForElements();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', () => flush(true));

    window.vulprImpression = { track: trackImpression };

    // Drain any impressions buffered by placements that rendered before init.
    const pending = window.vulprImpressionQueue;
    if (pending) {
        pending.forEach((tag) => trackImpression(tag));
        pending.length = 0;
    }
};
